import React, { useCallback, useMemo } from 'react';
import { isNull, isUndefined, uniq } from 'lodash/fp';

import styles from './AccessGroups.module.scss';
import { AccessGroup, AccessGroupTableName, clearAccessGroupFilters, getAccessGroupColumnSort, getAccessGroupFilters } from './store';
import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { getAllRoles, getFetchingRoles } from '../roles/store';
import { AccessGroupRolesTable } from './AccessGroupRolesTable';
import { AccessGroupUsersTable } from './AccessGroupUsersTable';
import { getAllUsers } from '../users/store';

const MODAL_HEADER_AND_PADDING = 55;
const TABLE_HEADER = 40;
const TABLE_TITLE = 40;
const SCROLLABLE_OFFSET = MODAL_HEADER_AND_PADDING + (TABLE_HEADER * 2) + (TABLE_TITLE * 2);
const NO_GROUPS_SCROLLABLE_OFFSET = 126;

interface AccessGroupUsersSectionProps {
    accessGroup: AccessGroup;
    updateGroup: (key: keyof AccessGroup, userIds: number[]) => void;
    modalHeight: number;
}

export const AccessGroupUsersSection: React.FC<AccessGroupUsersSectionProps> = ({ accessGroup, updateGroup, modalHeight }) => {
    const { userIds, roleIds } = accessGroup;
    const dispatch = useAppDispatch();

    const usersColumnSort = useAppSelector(getAccessGroupColumnSort(AccessGroupTableName.USER));
    const usersFilters = useAppSelector(getAccessGroupFilters(AccessGroupTableName.USER));
    const isFetchingRoles = useAppSelector(getFetchingRoles);
    const allRoles = useAppSelector(getAllRoles);
    const allUsers = useAppSelector(getAllUsers);

    const allUsersFromAccessGroupAndRoles = useMemo(() => uniq(userIds.concat(allUsers.filter(({ roleId }) => !isNull(roleId) && roleIds.includes(roleId)).map(({ userId }) => userId))), [allUsers, roleIds, userIds]);

    const rolesColumnSort = useAppSelector(getAccessGroupColumnSort(AccessGroupTableName.ROLE));
    const rolesFilters = useAppSelector(getAccessGroupFilters(AccessGroupTableName.ROLE));

    const clearUsersFilters = useCallback(() => dispatch(clearAccessGroupFilters(AccessGroupTableName.USER)), [dispatch]);
    const usersFiltersExist = useMemo(() => !!Object.values(usersFilters).filter(({ text, dropdown }) => !isNull(dropdown) || !!text).length || !isUndefined(usersColumnSort), [usersFilters, usersColumnSort]);

    const clearRolesFilters = useCallback(() => dispatch(clearAccessGroupFilters(AccessGroupTableName.ROLE)), [dispatch]);
    const rolesFiltersExist = useMemo(() => !!Object.values(rolesFilters).filter(({ text, dropdown }) => !isNull(dropdown) || !!text).length || !isUndefined(rolesColumnSort), [rolesFilters, rolesColumnSort]);
    const showRolesSection = useMemo(() => !isFetchingRoles && allRoles.length > 0, [isFetchingRoles, allRoles]);
    const rolesScrollableHeight = useMemo(() => showRolesSection ? ((modalHeight - SCROLLABLE_OFFSET) / 3) : 0, [modalHeight, showRolesSection]);
    const usersScrollableHeight = useMemo(() => showRolesSection ? ((modalHeight - SCROLLABLE_OFFSET) * 2 / 3) : (modalHeight - NO_GROUPS_SCROLLABLE_OFFSET), [modalHeight, showRolesSection]);

    return (
        <div className={styles.accessGroupContentWrapper}>
            {showRolesSection ? (
                <>
                    <div className={styles.tableTitleWrapper}>
                        <div className={styles.tableTitle}>Roles</div>
                        <div>({roleIds.length} selected)</div>
                        {rolesFiltersExist &&
                            <div className={styles.clearAllFiltersWrapper}>
                                <button className={styles.clearAllFilters} onClick={clearRolesFilters} data-testid='access-group-roles-filter-button'>Clear All Filters</button>
                            </div>
                        }
                    </div>
                    <AccessGroupRolesTable
                        roleIds={roleIds}
                        updateGroup={updateGroup}
                        scrollableHeight={rolesScrollableHeight}
                    />
                </>) : null}
            <div className={styles.tableTitleWrapper} style={showRolesSection ? { marginTop: '10px' } : {}}>
                <div className={styles.tableTitle}>Users</div>
                <div>({allUsersFromAccessGroupAndRoles.length} Selected)</div>
                {usersFiltersExist &&
                    <div className={styles.clearAllFiltersWrapper}>
                        <button className={styles.clearAllFilters} onClick={clearUsersFilters} data-testid='access-group-user-filter-button'>Clear All Filters</button>
                    </div>
                }
            </div>
            <AccessGroupUsersTable
                roleIds={roleIds}
                userIds={userIds}
                updateGroup={updateGroup}
                scrollableHeight={usersScrollableHeight}
            />
        </div>
    );
};
